/**
 * Auth Store — Stato globale autenticazione condiviso tra header, drawer, bottom-nav.
 *
 * Uso nei template:
 *   $store.auth.drawerOpen      — drawer visibile
 *   $store.auth.openDrawer()    — apre il login drawer
 *   $store.auth.login(u, p, r)  — esegue login
 */
export function registerAuthStore(Alpine) {
    Alpine.store('auth', {
        drawerOpen: false,
        registerDrawerOpen: false,
        registerTermsOpen: false,
        registerPrivacyOpen: false,
        loading: false,
        error: '',
        returnUrl: '',
        forcedReturn: false, // true solo se il drawer è stato aperto da un redirect requireAuth()
        inlineMode: false,  // true quando aperto come secondo pannello del checkout
        isLogged: false,
        user: { name: '', surname: '', fiscal_code: '' },

        // OTP (passwordless login)
        otpStep: 'login',   // 'login' | 'otp-request' | 'otp-verify'
        otpEmail: '',
        otpEmailHint: '',

        init() {
            if (window.IKOS?.authToken) {
                this.isLogged = true;
                this.user = window.IKOS.currentUser ?? { name: '', surname: '', fiscal_code: '' };
            }

            // Redirect da route protetta (requireAuth) per utente non loggato:
            // apre subito il drawer invece di lasciare l'utente su una homepage muta.
            // Se il server ha preservato la pagina di origine (loginReturnUrl), la
            // riusa come destinazione post-login (forcedReturn) — così un utente che
            // arriva da un link diretto a una pagina protetta (es. salvadanaio) ci
            // torna dopo il login, invece di atterrare sempre su /dashboard.
            // Rimuove ?login=/?return= dalla barra indirizzi per non riaprire il
            // drawer né ripetere il redirect a un refresh.
            if (window.IKOS?.autoOpenLogin && !this.isLogged) {
                this.openDrawer(window.IKOS.loginReturnUrl || undefined);
                this.forcedReturn = !!window.IKOS.loginReturnUrl;
                const url = new URL(window.location.href);
                url.searchParams.delete('login');
                url.searchParams.delete('return');
                window.history.replaceState({}, '', url);
            }
        },

        /**
         * Apre il drawer login.
         * @param {string} [returnUrl] — URL a cui tornare dopo il login
         */
        openDrawer(returnUrl) {
            this.returnUrl = returnUrl || window.location.href;
            this.forcedReturn = false;
            this.inlineMode = false;
            this.error = '';
            this.registerDrawerOpen = false;
            this.drawerOpen = true;
        },

        /**
         * Apre il drawer login in modalità inline (secondo pannello checkout).
         * Dopo login/register riusciti apre il preview invece di fare redirect.
         */
        openDrawerInline() {
            this.inlineMode = true;
            this.error = '';
            this.registerDrawerOpen = false;
            this.drawerOpen = true;
            sessionStorage.setItem('ik_checkout_pending', '1');
        },

        closeDrawer() {
            const wasInline = this.inlineMode;
            this.drawerOpen = false;
            this.inlineMode = false;
            this.error = '';
            this.otpStep = 'login';
            // Se chiude manualmente in checkout mode (es. tasto X, ESC, backdrop), chiude anche il checkout
            if (wasInline) {
                Alpine.store('checkout').close();
            }
        },

        /**
         * Chiude il drawer senza side-effect sul checkout (usato internamente dopo login riuscito).
         */
        _closeDrawerSilent() {
            this.drawerOpen = false;
            this.inlineMode = false;
            this.error = '';
            this.otpStep = 'login';
        },

        openRegisterDrawer(returnUrl) {
            this.returnUrl = returnUrl || window.location.href;
            this.error = '';
            this.drawerOpen = false;
            this.registerDrawerOpen = true;
        },

        /**
         * Apre il register drawer in modalità inline (secondo pannello checkout).
         */
        openRegisterDrawerInline() {
            this.error = '';
            this.drawerOpen = false;
            this.registerDrawerOpen = true;
        },

        closeRegisterDrawer() {
            const wasInline = this.inlineMode;
            this.registerDrawerOpen = false;
            this.inlineMode = false;
            this.error = '';
            if (wasInline) {
                Alpine.store('checkout').close();
            }
        },

        _closeRegisterDrawerSilent() {
            this.registerDrawerOpen = false;
            this.inlineMode = false;
            this.error = '';
        },

        openRegisterTerms() {
            this.registerTermsOpen = true;
        },

        closeRegisterTerms() {
            this.registerTermsOpen = false;
        },

        openRegisterPrivacy() {
            this.registerPrivacyOpen = true;
        },

        closeRegisterPrivacy() {
            this.registerPrivacyOpen = false;
        },

        // ── OTP (Passwordless) ──────────────────────────────────────────────────

        goToOtpRequest() {
            this.otpStep = 'otp-request';
            this.otpEmail = '';
            this.otpEmailHint = '';
            this.error = '';
        },

        goBackToLogin() {
            this.otpStep = 'login';
            this.error = '';
        },

        /**
         * Richiede invio OTP via email.
         * @param {string} email
         */
        async requestOtp(email) {
            this.error = '';
            this.loading = true;
            try {
                const res = await fetch(`${window.IKOS.ajaxBase}/otp-request.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const data = await res.json().catch(() => ({}));
                if (data.success) {
                    this.otpEmail     = email;
                    this.otpEmailHint = data.email_hint || '';
                    this.otpStep      = 'otp-verify';
                } else {
                    this.error = data.message || 'Errore durante l\'invio del codice.';
                }
            } catch {
                this.error = 'Errore di connessione. Riprova.';
            } finally {
                this.loading = false;
            }
        },

        /**
         * Verifica il codice OTP e logga l'utente.
         * @param {string} code — 6 cifre
         */
        async verifyOtp(code) {
            this.error = '';
            this.loading = true;
            // Per OTP usiamo solo inlineMode, non sessionStorage (evita falsi positivi da sessioni precedenti)
            const isInline = this.inlineMode;
            try {
                const cartToken = localStorage.getItem('ik_cart_token');
                const body = { email: this.otpEmail, code };
                if (cartToken) body.cart_token = cartToken;

                const res = await fetch(`${window.IKOS.ajaxBase}/otp-verify.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const data = await res.json().catch(() => ({}));
                if (data.success) {
                    localStorage.removeItem('ik_cart_token');
                    this.otpStep = 'login';
                    window.IKOS.authToken = data.auth_token || true;
                    this.isLogged = true;
                    this.user = data.user ?? { name: '', surname: '' };
                    this._closeDrawerSilent();
                    await Alpine.store('cart').refresh();
                    Alpine.store('wishlist').refresh();
                    if (isInline) {
                        sessionStorage.removeItem('ik_checkout_pending');
                        if (Alpine.store('cart').hasProfessionMismatch) {
                            Alpine.store('checkout').close();
                            Alpine.store('cart').openDrawer();
                        } else {
                            Alpine.store('checkout').openPreview();
                        }
                    } else {
                        window.dispatchEvent(new CustomEvent('otp-login-success'));
                        sessionStorage.setItem('ik_login_method', 'otp');
                        sessionStorage.setItem('ik_auth_just_logged', '1');
                        sessionStorage.setItem('ik_wishlist_just_logged', '1');
                        window.location.href = (this.forcedReturn && this.returnUrl) ? this.returnUrl : '/dashboard';
                    }
                } else {
                    this.error = data.message || 'Codice non valido.';
                }
            } catch {
                this.error = 'Errore di connessione. Riprova.';
            } finally {
                this.loading = false;
            }
        },

        // ────────────────────────────────────────────────────────────────────────

        /**
         * Esegue login via PHP proxy.
         * @param {string} username — email, telefono o codice fiscale
         * @param {string} password
         * @param {boolean} rememberMe
         */
        async login(username, password, rememberMe) {
            this.error = '';
            this.loading = true;
            // Legge inlineMode anche da sessionStorage per sopravvivere a eventuali reload
            const isInline = this.inlineMode || sessionStorage.getItem('ik_checkout_pending') === '1';

            try {
                // Manda il cart_token guest al server così il backend può fare il merge
                const cartToken = localStorage.getItem('ik_cart_token');
                const loginBody = {
                    username,
                    password,
                    remember_me: rememberMe,
                };
                if (cartToken) loginBody.cart_token = cartToken;

                const res = await fetch(`${window.IKOS.ajaxBase}/login.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(loginBody),
                });

                const data = await res.json().catch(() => ({}));

                if (data.success) {
                    // Il merge guest → utente è avvenuto, il cart_token guest non serve più
                    localStorage.removeItem('ik_cart_token');
                    sessionStorage.removeItem('ik_checkout_pending');
                    if (isInline) {
                        // In checkout mode: chiude silenziosamente e apre il preview
                        window.IKOS.authToken = data.auth_token || true;
                        this.isLogged = true;
                        this.user = data.user ?? { name: '', surname: '' };
                        this._closeDrawerSilent();
                        // Ri-fetcha il carrello: il merge backend potrebbe aver aggiunto
                        // corsi pre-esistenti dell'utente a quelli guest
                        await Alpine.store('cart').refresh();
                        if (Alpine.store('cart').hasProfessionMismatch) {
                            // Resetta lo shifting del checkout e apre il carrello normalmente
                            Alpine.store('checkout').close();
                            Alpine.store('cart').openDrawer();
                        } else {
                            Alpine.store('checkout').openPreview();
                        }
                        Alpine.store('wishlist').refresh();
                    } else {
                        // Segnala che il prossimo page load deve ri-fetchare cart e wishlist
                        sessionStorage.setItem('ik_login_method', 'password');
                        sessionStorage.setItem('ik_auth_just_logged', '1');
                        sessionStorage.setItem('ik_wishlist_just_logged', '1');
                        window.location.href = (this.forcedReturn && this.returnUrl) ? this.returnUrl : '/dashboard';
                    }
                } else {
                    this.error = data.message || 'Credenziali non valide.';
                }
            } catch {
                this.error = 'Errore di connessione. Riprova.';
            } finally {
                this.loading = false;
            }
        },
        /**
         * Esegue registrazione via PHP proxy.
         * @param {object} payload — dati form registrazione
         * @returns {Promise<{success, validation_messages}>}
         */
        async register(payload) {
            this.error = '';
            this.loading = true;

            try {
                // Manda il cart_token guest al server così il backend può fare il merge
                const cartToken = localStorage.getItem('ik_cart_token');
                const registerBody = { ...payload };
                if (cartToken) registerBody.cart_token = cartToken;

                const res = await fetch(`${window.IKOS.ajaxBase}/register.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(registerBody),
                });

                const data = await res.json().catch(() => ({}));

                if (data.success) {
                    // Il merge guest → utente è avvenuto, il cart_token guest non serve più
                    localStorage.removeItem('ik_cart_token');
                    const isInline = this.inlineMode || sessionStorage.getItem('ik_checkout_pending') === '1';
                    sessionStorage.removeItem('ik_checkout_pending');
                    if (isInline) {
                        // In checkout mode: chiude silenziosamente e apre il preview
                        window.IKOS.authToken = data.auth_token || true;
                        this.isLogged = true;
                        this.user = data.user ?? { name: '', surname: '' };
                        this._closeRegisterDrawerSilent();
                        // Ri-fetcha il carrello: il merge backend potrebbe aver aggiunto
                        // corsi pre-esistenti dell'utente a quelli guest
                        await Alpine.store('cart').refresh();
                        if (Alpine.store('cart').hasProfessionMismatch) {
                            // Resetta lo shifting del checkout e apre il carrello normalmente
                            Alpine.store('checkout').close();
                            Alpine.store('cart').openDrawer();
                        } else {
                            Alpine.store('checkout').openPreview();
                        }
                        Alpine.store('wishlist').refresh();
                    } else {
                        sessionStorage.setItem('ik_register_success', '1');
                        // Segnala che il prossimo page load deve ri-fetchare cart e wishlist
                        sessionStorage.setItem('ik_login_method', 'register');
                        // Id utente per l'event_id signup_{id} del tracking (dedup Pixel/CAPI):
                        // il redirect qui sotto distrugge il contesto JS, hooks.js lo legge all'arrivo.
                        try { if (data.user?.id) sessionStorage.setItem('ik_signup_user_id', String(data.user.id)); } catch (e) {}
                        sessionStorage.setItem('ik_auth_just_logged', '1');
                        sessionStorage.setItem('ik_wishlist_just_logged', '1');
                        window.location.href = (this.forcedReturn && this.returnUrl) ? this.returnUrl : '/dashboard';
                    }
                    // user (con id) serve al tracking per l'event_id signup_{id}
                    return { success: true, user: data.user ?? null };
                } else {
                    this.error = data.message || 'Errore durante la registrazione.';
                    return {
                        success: false,
                        validation_messages: data.validation_messages || {},
                    };
                }
            } catch {
                this.error = 'Errore di connessione. Riprova.';
                return { success: false, validation_messages: {} };
            } finally {
                this.loading = false;
            }
        },

        /**
         * Esegue logout via PHP proxy.
         */
        async logout() {
            // Pulisci cart token guest prima del reload
            localStorage.removeItem('ik_cart_token');
            try {
                await fetch(`${window.IKOS.ajaxBase}/logout.php`, {
                    method: 'POST',
                });
            } catch (e) {
                // ignora errori, procedi comunque col reload
            }
            window.location.reload();
        },
    });
}
